import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  AI_REPORT_TYPES,
  AiReport,
  AiReportKpi,
  AiReportTable,
} from 'src/app/shared/services/ai-reports.service';

const AI_NOTE_LABELS: Record<string, string> = {
  disabled: 'AI was turned off for this report.',
  not_configured: 'AI is not configured on the server (set AI_API_KEY).',
  provider_error: 'The AI provider returned an error.',
  invalid_json: 'The AI response could not be read.',
  invalid_schema: 'The AI response was incomplete.',
};

@Component({
  selector: 'app-ai-report-view',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ai-report-view.component.html',
  styleUrls: ['./ai-report-view.component.css'],
})
export class AiReportViewComponent {
  @Input({ required: true }) report!: AiReport;
  @Input() busy = false;
  @Input() showRegenerate = true;
  @Output() regenerate = new EventEmitter<void>();

  get typeLabel(): string {
    return AI_REPORT_TYPES.find((t) => t.type === this.report?.report_type)?.label || 'Report';
  }

  get aiNote(): string | null {
    const note = this.report?.narrative?.ai_note;
    if (!note || this.report.ai_used) return null;
    return AI_NOTE_LABELS[note] || 'AI was unavailable.';
  }

  get visibleTables(): AiReportTable[] {
    return (this.report?.facts?.tables || []).filter((t) => t.rows?.length);
  }

  get emptyTables(): AiReportTable[] {
    return (this.report?.facts?.tables || []).filter((t) => !t.rows?.length);
  }

  get emptyTableTitles(): string {
    return this.emptyTables.map((t) => t.title).join(', ');
  }

  formatKpi(k: AiReportKpi): string {
    if (k.value === null || k.value === undefined || k.value === '') return '—';
    const v = typeof k.value === 'number' ? this.formatNumber(k.value) : String(k.value);
    if (!k.unit) return v;
    if (k.unit === '%') return `${v}%`;
    if (/^[A-Z]{3}$/.test(k.unit)) return `${k.unit} ${v}`;
    return `${v} ${k.unit}`;
  }

  formatCell(value: any, numeric?: boolean): string {
    if (value === null || value === undefined || value === '') return '—';
    if (numeric && typeof value === 'number') return this.formatNumber(value);
    return String(value);
  }

  private formatNumber(n: number): string {
    return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  }

  private fileBase(): string {
    const name = (this.report.facts?.project?.name || 'project').replace(/[^\w-]+/g, '_').slice(0, 40);
    return `${this.report.report_type}_${name}_${this.report.id}`;
  }

  private pdfSafe(s: string): string {
    return String(s ?? '')
      .replace(/₹/g, 'INR ')
      .replace(/≥/g, '>=')
      .replace(/≤/g, '<=')
      .replace(/[^\x00-\xFF\u2013\u2014\u2018\u2019\u201C\u201D\u2022\u2026]/g, '');
  }

  exportPdf(): void {
    const r = this.report;
    const n = r.narrative;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const width = pageWidth - margin * 2;
    let y = 16;

    const ensure = (h: number) => {
      if (y + h > doc.internal.pageSize.getHeight() - 14) {
        doc.addPage();
        y = 16;
      }
    };
    const para = (text: string, size = 10, style: 'normal' | 'bold' = 'normal') => {
      doc.setFont('helvetica', style);
      doc.setFontSize(size);
      const lines = doc.splitTextToSize(this.pdfSafe(text), width);
      ensure(lines.length * size * 0.45 + 2);
      doc.text(lines, margin, y);
      y += lines.length * size * 0.45 + 2;
    };
    const list = (title: string, items: string[]) => {
      if (!items?.length) return;
      y += 2;
      para(title, 11, 'bold');
      items.forEach((it) => para(`• ${it}`));
    };

    para(r.title, 14, 'bold');
    doc.setTextColor(110);
    para(`${this.typeLabel} · Generated ${new Date(r.created_at).toLocaleString()} · ${r.ai_used ? `AI narrative (${r.model || 'LLM'})` : 'Rule-based narrative (AI off)'}`, 8);
    doc.setTextColor(0);
    y += 2;
    para(n.headline, 12, 'bold');
    para(n.summary);

    const kpis = r.facts?.kpis || [];
    if (kpis.length) {
      autoTable(doc, {
        startY: y + 2,
        head: [kpis.slice(0, 4).map((k) => this.pdfSafe(k.label))],
        body: [kpis.slice(0, 4).map((k) => this.pdfSafe(this.formatKpi(k)))],
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [37, 99, 235] },
        margin: { left: margin, right: margin },
      });
      y = (doc as any).lastAutoTable.finalY;
      if (kpis.length > 4) {
        autoTable(doc, {
          startY: y,
          head: [kpis.slice(4, 8).map((k) => this.pdfSafe(k.label))],
          body: [kpis.slice(4, 8).map((k) => this.pdfSafe(this.formatKpi(k)))],
          styles: { fontSize: 9, halign: 'center' },
          headStyles: { fillColor: [37, 99, 235] },
          margin: { left: margin, right: margin },
        });
        y = (doc as any).lastAutoTable.finalY;
      }
      y += 6;
    }

    list('Risks and issues', (n.risks || []).map((x) => `[${x.severity.toUpperCase()}] ${x.text}`));
    list('Highlights', n.highlights);
    list('Recommendations', n.recommendations);
    list('Data gaps', n.data_gaps);

    for (const t of this.visibleTables) {
      ensure(20);
      y += 4;
      para(t.title + (t.truncated ? ` (top ${t.rows.length} of ${t.total_rows})` : ''), 11, 'bold');
      autoTable(doc, {
        startY: y,
        head: [t.columns.map((c) => this.pdfSafe(c.label))],
        body: t.rows.map((row) => t.columns.map((c) => this.pdfSafe(this.formatCell(row[c.key], c.numeric)))),
        styles: { fontSize: 8, cellPadding: 1.5 },
        headStyles: { fillColor: [55, 65, 81] },
        columnStyles: Object.fromEntries(
          t.columns.map((c, i) => [i, c.numeric ? { halign: 'right' as const } : {}])
        ),
        margin: { left: margin, right: margin },
      });
      y = (doc as any).lastAutoTable.finalY + 2;
    }

    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(140);
      doc.text(
        'Figures come from project records. Narrative is AI-assisted and should be reviewed.',
        margin,
        doc.internal.pageSize.getHeight() - 8
      );
      doc.text(`${i} / ${pages}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
    }
    doc.save(`${this.fileBase()}.pdf`);
  }

  exportExcel(): void {
    const r = this.report;
    const n = r.narrative;
    const wb = XLSX.utils.book_new();

    const summaryRows: any[][] = [
      ['Report', r.title],
      ['Type', this.typeLabel],
      ['Generated', new Date(r.created_at).toLocaleString()],
      ['Narrative', r.ai_used ? `AI (${r.model || 'LLM'})` : 'Rule-based (AI off)'],
      [],
      ['Headline', n.headline],
      ['Summary', n.summary],
      [],
      ['KPI', 'Value'],
      ...(r.facts?.kpis || []).map((k) => [k.label, this.formatKpi(k)]),
      [],
      ['Risks'],
      ...(n.risks || []).map((x) => [x.severity, x.text]),
      [],
      ['Highlights'],
      ...(n.highlights || []).map((x) => ['', x]),
      [],
      ['Recommendations'],
      ...(n.recommendations || []).map((x) => ['', x]),
      [],
      ['Data gaps'],
      ...(n.data_gaps || []).map((x) => ['', x]),
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryRows);
    summarySheet['!cols'] = [{ wch: 22 }, { wch: 100 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    const used = new Set<string>(['Summary']);
    for (const t of this.visibleTables) {
      const rows = [t.columns.map((c) => c.label), ...t.rows.map((row) => t.columns.map((c) => row[c.key] ?? ''))];
      const sheet = XLSX.utils.aoa_to_sheet(rows);
      sheet['!cols'] = t.columns.map((c) => ({ wch: c.numeric ? 14 : 28 }));
      let name = t.title.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31).trim() || t.key;
      let i = 2;
      while (used.has(name)) name = `${name.slice(0, 28)} ${i++}`;
      used.add(name);
      XLSX.utils.book_append_sheet(wb, sheet, name);
    }
    XLSX.writeFile(wb, `${this.fileBase()}.xlsx`);
  }
}
